import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls, selectCls, textareaCls } from "./form-controls";

interface AvailabilityItem {
  _id: string;
  type: "blackout" | "preferred";
  startDate: string;
  endDate: string;
  notes: string | null;
}

// Single dialog handling all tenant availability CRUD: lists
// existing windows with delete-each, plus a form below to add a new
// one. When opened from a calendar slot click, the form is
// pre-filled with the clicked range.
export function AvailabilityDialog({
  open,
  onClose,
  initialStartDate,
  initialEndDate,
}: {
  open: boolean;
  onClose: () => void;
  initialStartDate?: string;
  initialEndDate?: string;
}) {
  const items = useQuery(
    anyApi.availability.listMine,
    open ? {} : "skip",
  ) as AvailabilityItem[] | undefined;
  const create = useMutation(anyApi.availability.create);
  const remove = useMutation(anyApi.availability.remove);

  const [type, setType] = useState<"blackout" | "preferred">("blackout");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setType("blackout");
    setStartDate(initialStartDate ?? "");
    setEndDate(initialEndDate ?? initialStartDate ?? "");
    setNotes("");
    setError(null);
  }, [open, initialStartDate, initialEndDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await create({
        type,
        startDate,
        endDate,
        notes: notes || undefined,
      });
      // Reset add-form fields but keep the dialog open so the new
      // entry shows up in the list above immediately.
      setType("blackout");
      setStartDate("");
      setEndDate("");
      setNotes("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm("Remove this availability window?")) return;
    await remove({ availabilityId: id });
  };

  const hasItems = items !== undefined && items.length > 0;

  return (
    <Dialog open={open} onClose={onClose} title="Availability" width="lg">
      <div className="px-6 py-5 space-y-6">
        <ExistingList
          items={items}
          onRemove={handleRemove}
        />

        <section>
          <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
            {hasItems ? "Add another window" : "Add a window"}
          </h3>
          <form onSubmit={handleSubmit} className="mt-3 space-y-4">
            <Field label="Type" required>
              <select
                value={type}
                onChange={(e) =>
                  setType(e.target.value as "blackout" | "preferred")
                }
                className={selectCls}
              >
                <option value="blackout">
                  Blackout — please don't schedule maintenance
                </option>
                <option value="preferred">
                  Preferred — these dates work best
                </option>
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="From" required>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className={inputCls}
                />
              </Field>
              <Field label="Through" required>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  min={startDate || undefined}
                  className={inputCls}
                />
              </Field>
            </div>
            <Field label="Notes (optional)">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Why? Any context for the landlord."
                className={`${textareaCls} resize-none`}
              />
            </Field>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={submitting || !startDate || !endDate}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Add window"}
              </button>
            </div>
          </form>
        </section>

      </div>
    </Dialog>
  );
}

function ExistingList({
  items,
  onRemove,
}: {
  items: AvailabilityItem[] | undefined;
  onRemove: (id: string) => void;
}) {
  if (items === undefined) {
    return (
      <section>
        <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
          Your windows
        </h3>
        <p className="mt-3 text-sm text-slate-500">Loading…</p>
      </section>
    );
  }
  if (items.length === 0) {
    return (
      <section>
        <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
          Your windows
        </h3>
        <p className="mt-3 text-sm text-slate-500">
          You haven't added any yet — use the form below.
        </p>
      </section>
    );
  }
  return (
    <section>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        Your windows
      </h3>
      <ul className="mt-3 divide-y divide-slate-100">
        {items.map((item) => (
          <li
            key={item._id}
            className="py-3 flex items-start justify-between gap-4 first:pt-0"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge type={item.type} />
                <p className="text-sm font-medium text-slate-900">
                  {formatRange(item.startDate, item.endDate)}
                </p>
              </div>
              {item.notes && (
                <p className="mt-0.5 text-sm text-slate-500">{item.notes}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(item._id)}
              className="shrink-0 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TypeBadge({ type }: { type: "blackout" | "preferred" }) {
  const styles = {
    blackout: "bg-rose-50 text-rose-700 ring-rose-200",
    preferred: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[type]}`}
    >
      {type}
    </span>
  );
}

function formatRange(start: string, end: string): string {
  const startD = new Date(`${start}T00:00:00`);
  const endD = new Date(`${end}T00:00:00`);
  const sameDay = start === end;
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year:
      startD.getFullYear() !== new Date().getFullYear() ||
      endD.getFullYear() !== new Date().getFullYear()
        ? "numeric"
        : undefined,
  };
  if (sameDay) return startD.toLocaleDateString("en-US", opts);
  return `${startD.toLocaleDateString("en-US", opts)} – ${endD.toLocaleDateString("en-US", opts)}`;
}
